import { AnimationVariants, StyledComponentProps } from '../types';
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../i18n';

const PartnersContainer = styled.section`
  padding: 100px 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
`;

const PartnerCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  border: 1px solid ${props => props.theme.colors.borderLight};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const PartnerLogo = styled.div<StyledComponentProps>`
  width: 120px;
  height: 120px;
  background: ${props => props.gradient || props.theme.gradients.primary};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  margin: 0 auto 24px;
  font-weight: bold;
`;

const PartnerName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 16px;
`;

const PartnerDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const PartnerFeatures = styled.div`
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: ${props => props.theme.colors.textLight};
  
  .feature-icon {
    width: 20px;
    height: 20px;
    background: ${props => props.theme.colors.success};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: white;
  }
`;

interface PartnersProps {
  id?: string;
}

const Partners: React.FC<PartnersProps> = ({ id }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const partners = [
    {
      name: "Dify",
      logo: "D",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      description: t('partners.dify.desc'),
      features: [
        t('partners.dify.f1'),
        t('partners.dify.f2'),
        t('partners.dify.f3'),
        t('partners.dify.f4')
      ]
    },
    {
      name: "FastGPT",
      logo: "F",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      description: t('partners.fastgpt.desc'),
      features: [
        t('partners.fastgpt.f1'),
        t('partners.fastgpt.f2'),
        t('partners.fastgpt.f3'),
        t('partners.fastgpt.f4')
      ]
    },
    {
      name: "Ragflow",
      logo: "R",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      description: t('partners.ragflow.desc'),
      features: [
        t('partners.ragflow.f1'),
        t('partners.ragflow.f2'),
        t('partners.ragflow.f3'),
        t('partners.ragflow.f4')
      ]
    }
  ];

  const containerVariants: AnimationVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants: AnimationVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <PartnersContainer id={id}>
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            {t('partners.section.title')}
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('partners.section.subtitle')}
          </SectionSubtitle>
        </SectionHeader>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <PartnersGrid>
            {partners.map((partner, index) => (
              <PartnerCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PartnerLogo gradient={partner.gradient}>
                  {partner.logo}
                </PartnerLogo>
                <PartnerName>{partner.name}</PartnerName>
                <PartnerDescription>{partner.description}</PartnerDescription>
                <PartnerFeatures>
                  {partner.features.map((feature, featureIndex) => (
                    <FeatureItem key={featureIndex}>
                      <div className="feature-icon">✓</div>
                      <span>{feature}</span>
                    </FeatureItem>
                  ))}
                </PartnerFeatures>
              </PartnerCard>
            ))}
          </PartnersGrid>
        </motion.div>

        {/* <CTASection>
          <CTAContent>
            <CTATitle>立即加入我们的生态</CTATitle>
            <CTADescription>
              与全球AI精英一起，构建企业级AI应用生态，开启智能商业新蓝海
            </CTADescription>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              立即体验
            </CTAButton>
          </CTAContent>
        </CTASection> */}
      </Container>
    </PartnersContainer>
  );
};

export default Partners;
